<script lang="ts" context="module">
  import { writable } from "svelte/store";

  import { mediaQueryStore } from "$lib/media-queries";

  // At this screen size, the drawer will be full screen.
  const fullscreen = mediaQueryStore("(max-width: 540px)");

  // This is the width saved for all drawers on the page. It is only preserved
  // during the browser session and isn't set in local storage.
  const drawerWidth = writable("calc(min(840px, 75%))");
</script>

<script lang="ts">
  import { X } from "lucide-svelte";
  import { createEventDispatcher } from "svelte";
  import { fly } from "svelte/transition";

  import GlobalHotKey from "$lib/ui/common/GlobalHotKey.svelte";
  import Portal from "./Portal.svelte";

  const dispatch = createEventDispatcher<{ close: void }>();

  export let open: boolean;
  export let showCloseButton: boolean = false;

  let drawer: HTMLElement;
  let dragging = false; // Set when the drawer is currently being resized.

  // Minimum width for manually resizing the drawer.
  const minResizeWidth = 600;

  // Svelte action for detecting clicks outside of the drawer element.
  function clickOutside(node: HTMLElement) {
    const handleClick = (event: MouseEvent) => {
      if (
        node &&
        !node.contains(event.target as Node) &&
        !event.defaultPrevented
      ) {
        node.dispatchEvent(new CustomEvent("close", { detail: node }));
      }
    };

    document.addEventListener("click", handleClick, true);

    return {
      destroy() {
        document.removeEventListener("click", handleClick, true);
      },
    };
  }

  function update(x: number) {
    const { right, width } = drawer.getBoundingClientRect();
    const newWidth = right - x;
    if (
      (newWidth >= width || newWidth >= minResizeWidth) &&
      newWidth < document.body.clientWidth - 24
    ) {
      drawerWidth.set(`${newWidth}px`);
    }
  }

  function drag(node: HTMLElement, callback: (event: PointerEvent) => void) {
    // Based on https://github.com/Rich-Harris/svelte-split-pane/blob/v1.1.3/src/lib/SplitPane.svelte
    const pointerdown = (event: PointerEvent) => {
      if (
        (event.pointerType === "mouse" && event.button === 2) ||
        (event.pointerType !== "mouse" && !event.isPrimary)
      )
        return;

      node.setPointerCapture(event.pointerId);

      event.preventDefault();

      dragging = true;

      const onpointerup = () => {
        dragging = false;

        node.setPointerCapture(event.pointerId);

        window.removeEventListener("pointermove", callback, false);
        window.removeEventListener("pointerup", onpointerup, false);
      };

      window.addEventListener("pointermove", callback, false);
      window.addEventListener("pointerup", onpointerup, false);
    };

    node.addEventListener("pointerdown", pointerdown, {
      capture: true,
      passive: false,
    });

    return {
      destroy() {
        node.removeEventListener("pointerdown", pointerdown);
      },
    };
  }
</script>

{#if open}
  <GlobalHotKey hotKey="Escape" on:hotkey={() => dispatch("close")} />

  <Portal>
    <div class="relative">
      <div
        class="drawer"
        style:width={$fullscreen ? "100%" : $drawerWidth}
        class:fullscreen={$fullscreen}
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        use:clickOutside
        on:close
        transition:fly={{ x: 200, duration: 250 }}
        bind:this={drawer}
      >
        <div class="h-full overflow-y-auto overscroll-contain">
          {#if showCloseButton}
            <button
              class="float-right p-1 mt-7 mr-7 rounded-md transition-colors hover:bg-white/10"
              on:click={() => dispatch("close")}><X size="20" /></button
            >
          {/if}

          <slot />
        </div>

        <!-- Divider element for resizing the drawer horizontally. -->
        {#if !$fullscreen}
          <div
            class="absolute flex justify-center -left-1 top-0 bottom-0 w-2 text-transparent hover:text-zinc-500 transition-colors duration-300 ease-in-out cursor-ew-resize"
            class:text-zinc-500={dragging}
            use:drag={(e) => update(e.clientX)}
          >
            <div class="border-r-2 border-current"></div>
          </div>
        {/if}
      </div>
    </div>
  </Portal>
{/if}

<style lang="postcss">
  .drawer {
    @apply fixed top-0 right-0 h-full bg-ground border-l border-zinc-800 z-40;
    box-shadow: 0px 0px 32px 6px rgba(0, 0, 0, 0.3);
    max-width: 100vw;
  }

  .drawer.fullscreen {
    border-left: 0;
  }
</style>
